import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Activity, Download } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { companyService } from '@/services/companyService';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { decodeHtmlEntities } from '@/lib/utils';

interface SlowItem {
  company_id: number;
  sku_id: string;
  company_sku_key: string;
  brand: string | null;
  product_name: string | null;
  sku_name_chi?: string | null;
  average_cost: number | null;
  merchant_inventory_qty: number | null;
  sku_status: string | null;
  total_value: number | null;
}

const ANALYTICS_CATEGORY: CategoryKey = 'analytics';

const SlowMovingProducts: React.FC = () => {
  const [items, setItems] = useState<SlowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { language } = useLanguage();

  const labels = useMemo(() => {
    if (language === 'zh') {
      return {
        pageTitle: '慢流產品分析（近30天銷售數據分析）',
        listTitle: '零銷售記錄清單（按總值高到低）',
        sku: '產品編碼',
        brand: '品牌',
        productName: '產品名稱',
        cost: '成本',
        qty: '數量',
        state: '狀態',
        totalValue: '總值',
        back: '返回儀表板',
        exporting: '匯出Excel',
        noData: '無資料',
        summaryZeroSkus: '零銷售的產品',
        summaryTotalQty: '總數目',
        summaryTotalValue: '總數價值',
      };
    }
    return {
      pageTitle: 'Slow-moving Products (30-day Sales Analysis)',
      listTitle: 'Zero-sales List (sorted by Total Value desc)',
      sku: 'SKU',
      brand: 'Brand',
      productName: 'Product Name',
      cost: 'Cost',
      qty: 'Qty',
      state: 'State',
      totalValue: 'Total Value',
      back: 'Back to Dashboard',
      exporting: 'Export Excel',
      noData: 'No data',
      summaryZeroSkus: 'Zero-sales Products',
      summaryTotalQty: 'Total Qty',
      summaryTotalValue: 'Total Value',
    };
  }, [language]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const company = await companyService.getCurrentCompany();
        if (!company?.id) {
          toast.error('未能取得公司資料');
          return;
        }
        // Call RPC created in supabase/create_rpc_slow_moving_products.sql
        const { data, error } = await supabase.rpc('slow_moving_products', { p_company_id: Number(company.id) });
        if (error) throw error;
        setItems((data as SlowItem[]) || []);
      } catch (err: any) {
        console.error(err);
        toast.error('載入慢流產品失敗: ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    // auto load once on enter
    load();
  }, []);

  const formatCurrency = (value: number | null | undefined) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v);
  };

  const exportToExcel = async () => {
    try {
      if (!items.length) {
        toast.info('沒有可匯出的資料');
        return;
      }
      const XLSX = await import('xlsx');
      const headers = ['SKU','Brand','Product Name','Cost','Qty','State','Total Value'];
      const aoa = [headers, ...items.map(i => {
        const name = language === 'zh' ? (i.sku_name_chi || i.product_name || '') : (i.product_name || '');
        return [
          String(i.sku_id ?? ''),
          i.brand ?? '',
          decodeHtmlEntities(name),
          Number(i.average_cost || 0),
          Number(i.merchant_inventory_qty || 0),
          i.sku_status ?? '',
          Number(i.total_value || 0),
        ];
      })];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // Force Column A (SKU) to text so leading zeros are preserved
      for (let r = 2; r <= aoa.length; r++) {
        const addr = 'A' + r;
        const cell = (ws as any)[addr];
        if (cell) {
          cell.t = 's'; // set as string
          cell.z = '@'; // text format
          if (cell.v != null) cell.v = String(cell.v);
        }
      }
      (ws as any)['!cols'] = [
        { wch: 16 }, { wch: 20 }, { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 16 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'SlowMoving');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'slow_moving_products.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e);
      toast.error('匯出失敗');
    }
  };

  const summary = useMemo(() => {
    const zeroSkus = items.length;
    const totalQty = items.reduce((s, it) => s + Number(it.merchant_inventory_qty || 0), 0);
    const totalVal = items.reduce((s, it) => s + Number(it.total_value || 0), 0);
    return { zeroSkus, totalQty, totalVal };
  }, [items]);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', ANALYTICS_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {labels.back}
          </Button>
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{labels.pageTitle}</h1>
          </div>
          <div className="flex-1" />
          <Button onClick={exportToExcel} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Download className="w-4 h-4 mr-2" /> {labels.exporting}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card/80 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{labels.summaryZeroSkus}</p>
                  <p className="text-3xl font-bold text-foreground">{summary.zeroSkus.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{labels.summaryTotalQty}</p>
                  <p className="text-3xl font-bold text-cyan-400">{summary.totalQty.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{labels.summaryTotalValue}</p>
                  <p className="text-3xl font-bold text-green-400">{formatCurrency(summary.totalVal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-card/80 border-border">
          <CardHeader>
            <CardTitle className="text-foreground">{labels.listTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div>
                <p className="text-muted-foreground mt-2">載入中...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-muted-foreground p-3">{labels.sku}</th>
                      <th className="text-left text-muted-foreground p-3">{labels.brand}</th>
                      <th className="text-left text-muted-foreground p-3">{labels.productName}</th>
                      <th className="text-right text-muted-foreground p-3">{labels.cost}</th>
                      <th className="text-right text-muted-foreground p-3">{labels.qty}</th>
                      <th className="text-left text-muted-foreground p-3">{labels.state}</th>
                      <th className="text-right text-muted-foreground p-3">{labels.totalValue}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx} className="border-b border-border hover:bg-accent/30">
                        <td className="text-foreground p-3">{it.sku_id}</td>
                        <td className="text-muted-foreground p-3">{it.brand || ''}</td>
                        <td className="text-muted-foreground p-3">{decodeHtmlEntities(language === 'zh' ? (it.sku_name_chi || it.product_name || '') : (it.product_name || ''))}</td>
                        <td className="text-muted-foreground p-3 text-right">{formatCurrency(it.average_cost)}</td>
                        <td className="text-muted-foreground p-3 text-right">{Number(it.merchant_inventory_qty || 0).toLocaleString()}</td>
                        <td className="text-muted-foreground p-3">{it.sku_status || ''}</td>
                        <td className="text-muted-foreground p-3 text-right">{formatCurrency(it.total_value)}</td>
                      </tr>
                    ))}
                    {!items.length && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted-foreground p-6">{labels.noData}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SlowMovingProducts;


